// import { AxiosResponse, AxiosError } from 'axios';
// interface IAPIResponse<T> {
//   status: 'success' | 'fail';
//   message: string;
//   data: T;
// }
// export const handleApiResponse = <T>(response: AxiosResponse<IAPIResponse<T>>) => {
//   return response.data;
// };
// export const handleApiError = (error: AxiosError) => {
//   if (error.response?.data?.error?.message) {
//     throw error.response.data.error.message;
//   } else {
//     throw error.response?.data.message;
//   }
// };
// export const apiGet = async <T>(
//   endpoint: string,
//   config?: Record<string, unknown>,
// ): Promise<T> => {
//   try {
//     const response = await mirageApi.get(endpoint, config);
//     return handleApiResponse<T>(response);
//   } catch (e) {
//     handleApiError(e);
//   }
// };
// export const getLeague = async (
//     leagueId: number,
// ): Promise<IAPIResponse<ILeague>> => {
//     return mirageApiGet(`/leagues/${leagueId}`);
// };
// export const getTeamRatings = async (
//     teamId: number,
// ): Promise<IAPIResponse<ITeam>> => {
//     return mirageApiGet(`/teams/${teamId}/ratings`);
// };
// export const getLeagues = async (): Promise<IAPIResponse<ILeague[]>> => {
//     return mirageApiGet("/leagues");
// };
// In this example, apiGet is a generic function that handles the common logic for making API calls using axios. The handleApiResponse and handleApiError functions are used to process the API response or handle errors consistently across different API calls. The generic type parameter <T> allows you to specify the type of the expected data in the API response.
import axios, {
    AxiosError,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";

import { API_URL, SITE_ID } from "./globals";
import { getTokenFromLocalStorage } from "./utils";

import { ICreateGameData } from "../components/CreateGame/types/creategame";
import { IRescheduleGameData } from "../components/RescheduleGame/types/reschedulegame";
import { IClubhouseCOIData } from "../pages/Clubhouse/components/COI/types/coi";
import { IGameSummaryData } from "../pages/Clubhouse/components/GameSummary/types/gamesummary";
import { IClubhouseTeamLFPData } from "../pages/Clubhouse/components/LookingForPlayers/types/lookingforplayers";
import {
    IPlacement,
    IUserPlacement,
} from "../pages/Clubhouse/components/Placement/types/placement";
import { ITeamRating } from "../pages/Clubhouse/components/Ratings/types/ratings";
import { IClubhouseTeamInfoData } from "../pages/Clubhouse/components/Team/types/team";
import { IClubhouseTeamTryoutData } from "../pages/Clubhouse/components/Tryouts/types/tryouts";
import {
    IAPIResponse,
    IAgeLevel,
    ICOIInfo,
    ICoupon,
    IDesignation,
    IField,
    IGame,
    IGameLog,
    ILeague,
    ILeagueDirection,
    ILeagueSchedule,
    ILeagueSummary,
    ILookingForPlayers,
    IOrder,
    IOrderHistory,
    IOrganization,
    IPhoto,
    IPhotoLocation,
    IPlayer,
    IPlayoff,
    IPlayoffBracket,
    IPosition,
    IProduct,
    IProductCategory,
    IPromoCode,
    IPromoCodeType,
    ISeason,
    ISiteSetting,
    ISponsor,
    ITeam,
    ITeamSchedule,
    ITryout,
    IUmpireRating,
    IUser,
    TLeagueSeasons,
    TLeagueYears,
} from "../types/api";
import {
    IContactFormData,
    ICreateProductData,
    ICreatePromoCodeData,
    ILeagueMgmtFieldData,
    ILeagueMgmtLeagueInfoData,
    ILeagueMgmtOrgInfoData,
    IPhotoUploadFormData,
    IPlayerFormData,
    IRegisterCoach,
    IRegisterOrgUser,
    IRegisterTeamForm,
    ISiteSettingsForm,
    IUserProfileData,
} from "../types/forms";
import {
    ICredentials,
    ILocalStorageUserData,
    IPasswordResetParams,
} from "../types/users.js";

// Create an authorized API request instance
const api = axios.create({
    baseURL: API_URL,
    timeout: 20000,
    withCredentials: true,
});

const requestInterceptor = (config: InternalAxiosRequestConfig<any>) => {
    const token = getTokenFromLocalStorage();

    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers["X-Site-Id"] = SITE_ID;

    return config;
};

const responseInterceptors = {
    response: (response: AxiosResponse<any, any>) => {
        if (
            process.env.BUILD_ENV &&
            !["production", "test"].includes(process.env.BUILD_ENV)
        ) {
            console.log("API Response:", response.data);
        }

        if (response?.data?.token) {
            const userDataRaw = localStorage.getItem("userData");

            if (userDataRaw) {
                let userData: ILocalStorageUserData = JSON.parse(userDataRaw);
                userData.token = response.data.token;
                localStorage.setItem("userData", JSON.stringify(userData));
            }
        }

        return response;
    },
    error: (error: any) => {
        if (
            process.env.BUILD_ENV &&
            !["production", "test"].includes(process.env.BUILD_ENV)
        ) {
            console.log("API Error:", error);
            error.response.data.error.message =
                error.response.data.error.message +
                "\n\n" +
                `File: ${error.response.data.error.file}\n` +
                `Line: ${error.response.data.error.line}\n` +
                error.response.data.error.stack.join("\n");
        }

        return Promise.reject(error);
    },
};

api.interceptors.request.use(config => requestInterceptor(config));
api.interceptors.response.use(
    responseInterceptors.response,
    responseInterceptors.error,
);

export const getSiteSetting = async (
    settingName?: string,
): Promise<IAPIResponse & { setting: ISiteSetting | Array<ISiteSetting> }> => {
    const urlBase = `/site/settings/${SITE_ID}`;
    const url = settingName ? `${urlBase}/${settingName}` : urlBase;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProducts = async (
    activeOnly = true,
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .get(`/products`, { params: { activeOnly } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProductCategories = async (): Promise<
    IAPIResponse & { categories: IProductCategory[] }
> => {
    return api
        .get(`/products/categories`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProduct = async (
    productId: IProduct["id"],
): Promise<IAPIResponse & { product: IProduct }> => {
    return api
        .get(`/products/${productId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getProductYears = async (): Promise<
    IAPIResponse & { years: number[] }
> => {
    return api
        .get(`/products/years`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createProduct = async (
    product: ICreateProductData,
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
    } = product;
    return api
        .post(
            `/products`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateProduct = async (
    product: ICreateProductData,
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    const {
        ageLevel: { label: ageLevel },
        category,
        qty,
        year,
        price,
        active,
        startDate,
        endDate,
    } = product;
    return api
        .put(
            `/products/${productId}`,
            {
                ageLevel,
                category,
                qty,
                year,
                price,
                userId,
                active,
                startDate,
                endDate,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchiveProduct = async (
    productId: IProduct["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { products: IProduct[] }> => {
    return api
        .post(
            `/products/${productId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCodes = async (
    params?: object,
    all = false,
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .get(`/products/promocodes`, { params: { ...params, all } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

/* Used for promo code management */
export const getPromoCode = async (
    promoCodeId: IPromoCode["id"],
): Promise<IAPIResponse & { promoCode: IPromoCode }> => {
    return api
        .get(`/products/promocodes/${promoCodeId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPromoCodeTypes = async (): Promise<
    IAPIResponse & {
        promoCodeTypes: IPromoCodeType[];
    }
> => {
    return api
        .get(`/products/promocodes/types`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPromoCode = async (
    product: ICreatePromoCodeData,
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .post(
            `/products/promocodes`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePromoCode = async (
    product: ICreatePromoCodeData,
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    const {
        code,
        discount,
        description,
        maxUses,
        active,
        startDate,
        endDate,
        appliesTo,
        promoCodeType: { value: promoCodeType },
    } = product;
    return api
        .put(
            `/products/promocodes/${promoCodeId}`,
            {
                code,
                discount,
                description,
                maxUses,
                active,
                startDate,
                endDate,
                appliesTo,
                userId,
                promoCodeType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const archivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 1,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const unarchivePromoCode = async (
    promoCodeId: IPromoCode["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { promoCodes: IPromoCode[] }> => {
    return api
        .post(
            `/products/promocodes/${promoCodeId}/archive`,
            {
                archive: 0,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getFieldsList = async (
    gamesOnly?: boolean,
): Promise<IAPIResponse & { fields: IField[] }> => {
    const endpoint = gamesOnly ? `/games/fields` : `/leagues/fields`;
    return api
        .get(endpoint)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getField = async (
    fieldId: number,
): Promise<IAPIResponse & { field: IField }> => {
    return api
        .get(`/leagues/fields/${fieldId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTryouts = async (): Promise<
    IAPIResponse & { tryouts: ITryout[] }
> => {
    return api
        .get("/teams/tryouts")
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLooking = async (): Promise<
    IAPIResponse & { looking: ILookingForPlayers[] }
> => {
    return api
        .get("/teams/lfp")
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAgeLevels = async () => {
    try {
        const response = await api.get(`/leagues/ageLevels`);

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getPositions = async (): Promise<
    IAPIResponse & { positions: IPosition[] }
> => {
    return api
        .get("/teams/positions")
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLeagues = async (year: ILeague["year"]) => {
    try {
        const response = await api.get(`/leagues`, {
            params: {
                year,
            },
        });

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getYears = async (): Promise<
    IAPIResponse & {
        years: {
            [year in TLeagueYears]: {
                [season in TLeagueSeasons]: IAgeLevel["ageLevel"][];
            };
        };
    }
> => {
    return api
        .get(`/leagues/years`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLeague = async (
    leagueId: number,
): Promise<IAPIResponse & { league: ILeague }> => {
    return api
        .get(`/leagues/${leagueId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getAllTeamRatings = async (
    year?: number,
): Promise<IAPIResponse & { ratings: ITeamRating[] }> => {
    return api
        .get(`/ratings/team`, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamRatings = async (
    teamId: number,
    gameId?: number,
    year?: number,
): Promise<IAPIResponse & { ratings: ITeamRating[] }> => {
    return api
        .get(`/ratings/team/${teamId}`, {
            params: {
                gameId,
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpireRatings = async (
    umpireId?: number,
    year?: number,
): Promise<IAPIResponse & { ratings: IUmpireRating[] }> => {
    return api
        .get(`/ratings/umpire${umpireId ? `/umpireId` : ""}`, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameRatings = async (
    gameId: number,
    type: "umpire" | "team",
): Promise<IAPIResponse & { ratings: Array<ITeamRating | IUmpireRating> }> => {
    return api
        .get(`/ratings/game/${gameId}`, {
            params: {
                ratingType: type,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLeagueSchedule = async (
    leagueId: number,
): Promise<IAPIResponse & { schedule: ILeagueSchedule }> => {
    return api
        .get(`/leagues/${leagueId}/schedule`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLeagueTeams = async (
    leagueId: number,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/leagues/${leagueId}/teams`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlayoffSchedule = async (leagueId: number) => {
    try {
        const response = await api.get(
            `/leagues/${leagueId}/schedule?playoffs=true`,
        );

        return response.data;
    } catch (e) {
        if (e instanceof AxiosError) {
            if (e.response?.data?.error?.message) {
                throw e.response.data.error.message;
            } else {
                throw e.response?.data.message;
            }
        }
    }
};

export const getBracket = async (
    leagueId: number,
): Promise<IAPIResponse & { bracket: IPlayoff }> => {
    return api
        .get(`/leagues/${leagueId}/bracket`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamSchedule = async (
    leagueId: number,
    teamId: number,
): Promise<IAPIResponse & { schedule: ITeamSchedule | null }> => {
    return api
        .get(`/leagues/${leagueId}/teams/${teamId}/schedule`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGame = async (
    gameId: IGame["id"],
): Promise<IAPIResponse & { game: IGame }> => {
    return api
        .get(`/games/${gameId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getGameLogs = async (
    gameId?: IGame["id"],
    year?: number,
): Promise<IAPIResponse & { logs: IGameLog[] }> => {
    const urlBase = `/logs/games`;
    const url = gameId ? `${urlBase}/${gameId}` : urlBase;

    return api
        .get(url, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeams = async (): Promise<
    IAPIResponse & { teams: ITeam[] }
> => {
    return api
        .get(`/teams`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeam = async (
    teamId: number,
): Promise<IAPIResponse & { team: ITeam }> => {
    return api
        .get(`/teams/${teamId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getClubhouseTeams = async (
    userId: number,
    isRegistering = false,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/teams`, {
            params: {
                userId: userId,
                isRegistering,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUser = async (
    user: IUser["id"],
): Promise<IAPIResponse & { user: IUser }> => {
    return api
        .get(`/users/${user}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUsers = async (): Promise<
    IAPIResponse & { users: IUser[] }
> => {
    return api
        .get(`/users`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getDesignations = async (): Promise<
    IAPIResponse & { designations: IDesignation[] }
> => {
    return api
        .get(`/leagues/designations`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getLeagueDirections = async (): Promise<
    IAPIResponse & { directions: ILeagueDirection[] }
> => {
    return api
        .get(`/leagues/directions`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSeasons = async (): Promise<
    IAPIResponse & { seasons: ISeason[] }
> => {
    return api
        .get(`/leagues/seasons`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getBracketTypes = async (): Promise<
    IAPIResponse & { brackets: IPlayoffBracket[] }
> => {
    return api
        .get(`/leagues/brackets`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamsByAgeLevel = async (
    ageLevel: string,
    year: number,
): Promise<IAPIResponse & { teams: ITeam[] }> => {
    return api
        .get(`/teams`, {
            params: {
                ageLevel,
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getTeamsBySite = async (): Promise<
    IAPIResponse & { teams: ITeam[] }
> => {
    return api
        .get(`/teams`, {
            params: {
                site: SITE_ID,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlayer = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    return api
        .get(`/players/${playerId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deletePlayer = async (
    playerId: IPlayer["id"],
    teamId: ITeam["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/players/${playerId}`, {
            data: {
                teamId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const verifyPlayer = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse & { player: IPlayer["id"] }> => {
    return api
        .get(`/players/verify/${playerId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getRoster = async (
    teamId: ITeam["id"],
    leagueId?: ILeague["id"],
): Promise<IAPIResponse & { players: IPlayer[] }> => {
    return api
        .get(`/teams/${teamId}/roster?leagueId=${leagueId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUmpires = async (): Promise<
    IAPIResponse & {
        umpires: Array<Omit<IUser, "password" | "meta"> & { name: string }>;
    }
> => {
    return api
        .get(`/users/umpires`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPlacementResults = async (
    year?: number,
): Promise<IAPIResponse & { placements: IPlacement[] }> => {
    return api
        .get(`/leagues/placements`, {
            params: {
                year: year || null,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getUserPlacementRatings = async (
    userId: number,
): Promise<IAPIResponse & { ratings: IUserPlacement[] }> => {
    return api
        .get(`/leagues/placements/${userId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePlacementRatings = async (
    teamId: number,
    rating: number,
    userId: number,
): Promise<IAPIResponse & { rating: IUserPlacement }> => {
    return api
        .post(
            `/leagues/placements/`,
            {
                teamId,
                rating,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const assignPlayoffSeeds = (leagueId: number, userId: number): void => {
    api.post(
        `/leagues/buildPlayoffs/${leagueId}`,
        {
            userId,
        },
        {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        },
    );
};

export const resetPlayoffSeeds = async (
    leagueId: number,
    userId: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/leagues/resetPlayoffs/${leagueId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPaymentIntent = async (
    cart: IRegisterTeamForm[],
    total: number,
    discount: number,
): Promise<IAPIResponse & { clientSecret: string }> => {
    return api
        .post(
            `/orders/paymentintent/`,
            {
                cart,
                total,
                discount,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getStripeOrder = async (
    paymentIntentId: string,
): Promise<IAPIResponse & { paymentStatus: string; order: IOrder }> => {
    return api
        .post(
            `/orders/stripestatus/`,
            {
                paymentIntentId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addOrderHistory = async (
    paymentIntentId: string,
    message: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/orders/history`,
            {
                paymentIntentId,
                message,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateOrder = async (
    orderId: IOrder["id"],
    orderStatus: IOrder["status"],
    orderNote?: IOrderHistory["message"],
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .put(
            `/orders/${orderId}`,
            {
                orderStatus,
                orderNote,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

/* Used during registration process to retrieve promo code */
export const getCoupon = async (
    couponCode: IPromoCode["code"],
): Promise<IAPIResponse & { coupon: ICoupon | null }> => {
    return api
        .get(`/orders/coupon`, { params: { couponCode } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrder = async (
    orderId: number,
): Promise<IAPIResponse & { order: IOrder }> => {
    return api
        .get(`/orders/${orderId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrders = async (
    year?: number,
): Promise<IAPIResponse & { orders: IOrder[] }> => {
    return api
        .get(`/orders`, {
            params: {
                year,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data) {
                    throw e.response.data;
                } else {
                    throw e.response;
                }
            }
        });
};

export const getOrganizations = async (
    orgId?: number,
): Promise<
    IAPIResponse &
        Partial<{ organizations: IOrganization[]; organization: IOrganization }>
> => {
    let url = "/organizations";
    url = orgId ? `${url}/${orgId}` : url;

    return api
        .get(url)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getCOI = async (
    teamId?: number,
    status?: string,
): Promise<IAPIResponse & { coi: ICOIInfo[] }> => {
    const URL = teamId ? `/teams/coi/${teamId}` : `/teams/coi`;
    const params = status && { status };

    return api
        .get(URL, { params })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const approveCOI = async (
    teamId: ITeam["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/approve`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePhoto = async (
    photoId: IPhoto["id"],
    location: IPhotoLocation["id"] | null,
    userId: IUser["id"],
): Promise<IAPIResponse & { photo: IPhoto }> => {
    return api
        .put(
            `/site/photos/${photoId}`,
            {
                location,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rejectCOI = async (
    teamId: ITeam["id"],
    message: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/reject`,
            {
                userId,
                message,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addCOIMessage = async (
    teamId: ITeam["id"],
    message: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/message`,
            {
                userId,
                message,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const addCOIExpiration = async (
    teamId: ITeam["id"],
    expiration: string,
    userId: IUser["id"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}/expiration`,
            {
                userId,
                expiration,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const loginUser = async (
    creds: ICredentials,
): Promise<IAPIResponse & ILocalStorageUserData> => {
    return api
        .post(
            `/users/authenticate`,
            {
                remember: creds.remember,
            },
            {
                auth: {
                    username: creds.email,
                    password: creds.password,
                },
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateUser = async (
    user: IUserProfileData,
): Promise<IAPIResponse> => {
    const { id, firstName, lastName, phone, email, password, confirmPassword } =
        user;

    return api
        .put(
            `/users/${user.id}`,
            {
                id,
                firstName,
                lastName,
                phone,
                email,
                password,
                confirmPassword,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const registerUser = async (
    user: IRegisterCoach | IRegisterOrgUser,
    userType: "coach" | "orguser",
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users`,
            {
                userType,
                user,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const verifyEmail = async (
    email: IUser["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/verifyemail`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createLeague = async (
    league: ILeagueMgmtLeagueInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { league: ILeague }> => {
    const {
        active,
        ageLevel: { value: ageLevel },
        bracket,
        season: { value: season },
        teams,
        excludedTeams,
        year,
        designation: { value: designation } = {},
        direction: { value: direction } = {},
        files,
        playoffDates,
    } = league;

    return api
        .post(
            `/leagues`,
            {
                active,
                ageLevel,
                bracket: bracket?.value,
                season,
                teams,
                excludedTeams,
                year,
                designation,
                direction,
                files,
                playoffDates,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createOrder = async (
    paymentType: string,
    cart: IRegisterTeamForm[],
    cartTotal: number,
    discount: number,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/orders`,
            {
                paymentType,
                cart,
                cartTotal,
                discount,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateLeague = async (
    leagueId: ILeague["id"],
    league: ILeagueMgmtLeagueInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { league: ILeague }> => {
    const {
        active,
        ageLevel: { value: ageLevel },
        bracket,
        season: { value: season },
        teams,
        excludedTeams,
        year,
        designation,
        direction,
        files,
        playoffDates,
    } = league;

    return api
        .put(
            `/leagues/${leagueId}`,
            {
                active,
                ageLevel,
                bracket: bracket?.value,
                season,
                teams,
                excludedTeams,
                year,
                designation: designation?.value,
                direction: direction?.value,
                files,
                playoffDates,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateTeam = async (
    teamId: ITeam["id"],
    team: IClubhouseTeamInfoData,
): Promise<IAPIResponse & { team: ITeam }> => {
    const {
        name,
        coach: { value: coach },
        bannedUmpire1,
        bannedUmpire2,
        files,
        settings,
    } = team;

    const banned1 = bannedUmpire1 ? bannedUmpire1.value : null;
    const banned2 = bannedUmpire2 ? bannedUmpire2.value : null;

    return api
        .put(
            `/teams/${teamId}`,
            {
                name,
                coach,
                bannedUmpire1: banned1,
                bannedUmpire2: banned2,
                files,
                settings,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const uploadPhotos = async (
    data: IPhotoUploadFormData,
): Promise<IAPIResponse> => {
    const {
        firstName,
        lastName,
        email,
        teamName,
        ageLevel: { value: ageLevel },
        files,
    } = data;

    return api
        .post(
            `/site/photoupload`,
            {
                firstName,
                lastName,
                email,
                teamName,
                ageLevel,
                files,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const editorImgUpload = async (file: File): Promise<string> => {
    return api
        .post(
            `/site/photoupload`,
            {
                files: [file],
                editorUpload: true,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data.location;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotos = async (
    location?: string,
): Promise<IAPIResponse & { photos: IPhoto[] }> => {
    return api
        .get(`/site/photos`, { params: { location } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const printRules = async (
    content: string,
): Promise<IAPIResponse & { file: string }> => {
    return api
        .get(`/printer`, { params: { content } })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rejectPhoto = async (
    photoId: IPhoto["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/site/photos/${photoId}`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getPhotoLocations = async (): Promise<
    IAPIResponse & { photoLocations: IPhotoLocation[] }
> => {
    return api
        .get(`/site/photolocations`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createField = async (
    field: ILeagueMgmtFieldData,
): Promise<IAPIResponse & { field: IField }> => {
    const { active, name, address, city, state, zip, diamonds } = field;

    return api
        .post(
            `/leagues/fields`,
            {
                active,
                name,
                address,
                city,
                state,
                zip,
                diamonds,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateField = async (
    field: ILeagueMgmtFieldData,
    fieldId: IField["id"],
): Promise<IAPIResponse & { field: IField }> => {
    const { active, name, address, city, state, zip, diamonds } = field;

    return api
        .put(
            `/leagues/fields/${fieldId}`,
            {
                active,
                name,
                address,
                city,
                state,
                zip,
                diamonds,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createGame = async (
    game: ICreateGameData,
    league: ILeague["id"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule | ILeagueSchedule }> => {
    const {
        datetime,
        home: { value: home },
        visitor: { value: visitor },
        field: { value: field },
        diamond,
        note,
    } = game;

    return api
        .post(
            `/games`,
            {
                datetime: datetime.toLocaleString("en-US"),
                home,
                visitor,
                field,
                diamond,
                note,
                league,
                userId,
                scheduleType,
                teamId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteGame = async (
    gameId: IGame["id"],
    userId: ILocalStorageUserData["user"]["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule | ILeagueSchedule }> => {
    return api
        .delete(`/games/${gameId}`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const resetGame = async (
    gameId: IGame["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule | ILeagueSchedule }> => {
    return api
        .post(`/games/${gameId}/reset`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const postponeGame = async (
    gameId: IGame["id"],
    leagueId: IGame["league"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule | ILeagueSchedule }> => {
    return api
        .put(
            `/games/${gameId}/postpone`,
            {
                userId,
                teamId,
                leagueId,
                scheduleType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const rescheduleGame = async (
    game: IRescheduleGameData,
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    leagueId: ILeague["id"],
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule | ILeagueSchedule }> => {
    const {
        gameId,
        datetime,
        field: { value: field },
        note,
    } = game;

    const diamond = game.diamond ? game.diamond.value : undefined;

    return api
        .put(
            `/games/${gameId}/reschedule`,
            {
                datetime: datetime.toLocaleString("en-US"),
                field,
                diamond,
                note,
                userId,
                scheduleType,
                leagueId,
                teamId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const acceptReschedule = async (
    gameId: IGame["id"],
    leagueId: IGame["league"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
    return api
        .put(
            `/games/${gameId}/acceptreschedule`,
            {
                userId,
                teamId,
                leagueId,
                scheduleType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const declineReschedule = async (
    gameId: IGame["id"],
    leagueId: IGame["league"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
    return api
        .put(
            `/games/${gameId}/declinereschedule`,
            {
                userId,
                teamId,
                leagueId,
                scheduleType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const cancelReschedule = async (
    gameId: IGame["id"],
    leagueId: IGame["league"],
    userId: ILocalStorageUserData["user"]["id"],
    scheduleType: "team" | "league",
    teamId?: ITeam["id"],
): Promise<IAPIResponse & { schedule: ITeamSchedule }> => {
    return api
        .put(
            `/games/${gameId}/cancelreschedule`,
            {
                userId,
                teamId,
                leagueId,
                scheduleType,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createPlayer = async (
    player: IPlayerFormData,
    teamId: ITeam["id"],
    leagueId?: ILeague["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    const {
        active,
        firstName,
        lastName,
        uniformNumber,
        dob,
        email,
        positions,
        isPrimary,
    } = player;

    return api
        .post(
            `/players`,
            {
                active,
                firstName,
                lastName,
                uniformNumber,
                dob,
                email,
                positions,
                teamId,
                isPrimary,
                leagueId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updatePlayer = async (
    player: IPlayerFormData,
    teamId: ITeam["id"],
): Promise<IAPIResponse & { player: IPlayer }> => {
    const {
        playerId,
        active,
        firstName,
        lastName,
        uniformNumber,
        dob,
        email,
        positions,
        isPrimary,
    } = player;

    return api
        .put(
            `/players/${playerId}`,
            {
                active,
                firstName,
                lastName,
                uniformNumber,
                dob,
                email,
                positions,
                teamId,
                isPrimary,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateSettings = async (
    data: Partial<ISiteSettingsForm>,
): Promise<IAPIResponse> => {
    return api
        .put(
            `/site/settings/${SITE_ID}`,
            {
                data,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createOrganization = async (
    organization: ILeagueMgmtOrgInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { organization: IOrganization }> => {
    const {
        name,
        primaryUser: { value: primaryUser },
        majorTeams,
        WITeams,
        ILTeams,
    } = organization;

    return api
        .post(
            `/organizations`,
            {
                name,
                primaryUser,
                majorTeams,
                WITeams,
                ILTeams,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateOrganization = async (
    orgId: IOrganization["id"],
    organization: ILeagueMgmtOrgInfoData,
    userId: IUser["id"],
): Promise<IAPIResponse & { organization: IOrganization }> => {
    const {
        name,
        primaryUser: { value: primaryUser },
        majorTeams,
        WITeams,
        ILTeams,
        approved,
    } = organization;

    return api
        .put(
            `/organizations/${orgId}`,
            {
                name,
                primaryUser,
                majorTeams,
                WITeams,
                ILTeams,
                approved,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateGameSummary = async (
    game: Partial<IGameSummaryData>,
    gameId: IGame["id"],
    userId: IUser["id"],
): Promise<IAPIResponse> => {
    const { scores, pitchCounts, homeSummary, visitorSummary } = game;

    return api
        .put(
            `/games/${gameId}/summary`,
            {
                scores,
                pitchCounts,
                homeSummary,
                visitorSummary,
                userId,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateUmpireRatings = async (
    game: Partial<IGameSummaryData>,
    gameId: IGame["id"],
    userId: IUser["id"],
    umpires: { plate?: number; field?: number },
): Promise<IAPIResponse> => {
    const { plate, field } = game;

    return api
        .put(
            `/ratings/game/${gameId}`,
            {
                plate,
                field,
                userId,
                umpires,
                type: "umpire",
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateLFP = async (
    lfp: IClubhouseTeamLFPData,
    teamId: ITeam["id"],
): Promise<IAPIResponse & { looking: ILookingForPlayers[] }> => {
    const {
        contactEmail,
        contactName,
        contactNumber,
        contactSite,
        description,
        positions,
        ageLevels,
    } = lfp;

    return api
        .put(
            `/teams/${teamId}/lfp`,
            {
                contactEmail,
                contactName,
                contactNumber,
                contactSite,
                description,
                positions,
                ageLevels,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateTryouts = async (
    tryouts: IClubhouseTeamTryoutData,
    teamId: ITeam["id"],
): Promise<IAPIResponse & { tryouts: ITryout[] }> => {
    const {
        contactEmail,
        contactName,
        contactNumber,
        contactSite,
        description,
        dates,
        field,
    } = tryouts;

    return api
        .put(
            `/teams/${teamId}/tryouts`,
            {
                contactEmail,
                contactName,
                contactNumber,
                contactSite,
                description,
                dates,
                field,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteTryouts = async (
    teamId: ITeam["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { tryouts: ITryout[] }> => {
    return api
        .delete(`/teams/${teamId}/tryouts`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteLooking = async (
    teamId: ITeam["id"],
    userId: IUser["id"],
): Promise<IAPIResponse & { looking: ILookingForPlayers[] }> => {
    return api
        .delete(`/teams/${teamId}/lfp`, {
            data: {
                userId,
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateResume = async (
    formData: string,
    teamId: ITeam["id"],
): Promise<IAPIResponse> => {
    return api
        .put(
            `/teams/${teamId}/resume`,
            {
                formData,
            },
            {
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateCOI = async (
    teamId: ITeam["id"],
    files: IClubhouseCOIData["files"],
): Promise<IAPIResponse & { coi: ICOIInfo }> => {
    return api
        .put(
            `/teams/coi/${teamId}`,
            {
                files,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const lostPassword = async (
    email: ICredentials["email"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/lostpassword/`,
            {
                email,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const sendWaiver = async (
    teamId: ITeam["id"],
    leagueId: ILeague["id"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/teams/${teamId}/waiver`,
            {
                leagueId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const acceptWaiver = async (
    playerId: IPlayer["id"],
): Promise<IAPIResponse> => {
    return api
        .post(`/players/${playerId}/waiver`, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const resetPassword = async ({
    userId,
    password,
    confirmPassword,
}: IPasswordResetParams): Promise<IAPIResponse & { email: IUser["email"] }> => {
    return api
        .post(
            `/users/resetpassword`,
            {
                userId,
                password,
                confirmPassword,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateTokens = async (): Promise<IAPIResponse> => {
    return api
        .get(`/users/validatetokens`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const validateResetToken = async (
    token: string,
    userId: string,
): Promise<IAPIResponse> => {
    return api
        .post(
            `/users/validateresettoken`,
            {
                token,
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const submitContact = async (
    data: IContactFormData,
): Promise<IAPIResponse> => {
    const {
        ageLevel: { label: ageLevel },
        comment,
        contactType,
        email,
        firstName,
        lastName,
        teamName,
    } = data;

    return api
        .post(
            `/home/contact`,
            {
                ageLevel,
                comment,
                contactType,
                email,
                firstName,
                lastName,
                teamName,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateStandings = async (
    leagueId: ILeague["id"],
    userId: IUser["id"],
): Promise<IAPIResponse> => {
    return api
        .post(
            `/leagues/standings?leagueId=${leagueId}`,
            {
                userId,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSponsors = async (): Promise<
    IAPIResponse & { sponsors: ISponsor[] }
> => {
    return api
        .get(`/site/sponsors`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSponsor = async (
    id: number,
): Promise<IAPIResponse & { sponsor: ISponsor }> => {
    return api
        .get(`/site/sponsors/${id}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const updateSponsor = async (
    id: ISponsor["id"],
    name: ISponsor["name"],
    link?: ISponsor["link"],
    logo?: File[],
): Promise<IAPIResponse & { sponsor: ISponsor }> => {
    return api
        .put(
            `/site/sponsors/${id}`,
            {
                name,
                link,
                logo,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            console.log(e);
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const createSponsor = async (
    name: ISponsor["name"],
    link?: ISponsor["link"],
    logo?: File[],
): Promise<IAPIResponse & { sponsor: ISponsor }> => {
    return api
        .post(
            `/site/sponsors`,
            {
                name,
                link,
                logo,
            },
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            },
        )
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteSponsor = async (
    id: ISponsor["id"],
): Promise<IAPIResponse & { sponsor: ISponsor }> => {
    return api
        .delete(`/site/sponsors/${id}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const deleteOrganization = async (
    organizationId: IOrganization["id"],
): Promise<IAPIResponse> => {
    return api
        .delete(`/organizations/${organizationId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const switchToUser = async (
    userId: IUser["id"],
): Promise<IAPIResponse & ILocalStorageUserData> => {
    return api
        .post(`/users/switch/${userId}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export const getSummary = async (
    id: ILeague["id"],
): Promise<IAPIResponse & { summary: ILeagueSummary }> => {
    return api
        .get(`/leagues/summary/${id}`)
        .then(response => {
            return response.data;
        })
        .catch(e => {
            if (e instanceof AxiosError) {
                if (e.response?.data?.error?.message) {
                    throw e.response.data.error.message;
                } else {
                    throw e.response?.data.message;
                }
            }
        });
};

export default api;
